import React, { useState, useEffect } from 'react';
import T from '../../components/common/T';
import { Link, useNavigate } from 'react-router-dom';
import { useInventory } from '../../context/InventoryContext';
// import Layout from '../../components/Layout';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// 🟢 SECURITY IMPORTS
import { useAuth } from '../../context/AuthContext';
import RoleGate from '../../components/RoleGate';
import PageLayout, { PageHeader } from '../../components/common/PageLayout';
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';

const WarehouseCard = ({ wh, addBinToWarehouse }) => {
    const [newBin, setNewBin] = useState("");
    
    // 🟢 GET CHECKER
    const { checkAccess } = useAuth();

    const handleAddBin = async (e) => {
        e.preventDefault();
        if (newBin.trim()) {
            await addBinToWarehouse(wh.id, newBin.trim());
            toast.success(`Bin "${newBin}" added to ${wh.name}`);
            setNewBin("");
        }
    };

    return (
        <div className="border border-gray-200 dark:border-[#30363d] p-5 rounded-xl bg-white dark:bg-[#161b22] shadow-xs hover:shadow-md dark:hover:border-slate-600 transition-all">
            <div className="flex justify-between items-start mb-2">
                <div>
                    <h3 className="font-bold text-base text-gray-900 dark:text-white">{wh.name}</h3>
                    <p className="text-xs text-gray-500 dark:text-slate-400 font-mono">ID: {wh.id}</p>
                </div>
                <span className="bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 text-xs font-semibold px-2.5 py-0.5 rounded-full border border-blue-100 dark:border-blue-800/60">
                    {wh.bins?.length || 0} Bins
                </span>
            </div>
            
            <div className="mt-4 mb-4 flex flex-wrap gap-2">
                {wh.bins?.slice(0, 5).map(bin => (
                    <span key={bin} className="bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-300 px-2 py-1 rounded text-xs border border-gray-200 dark:border-slate-700">
                        {bin}
                    </span>
                ))}
                {(wh.bins?.length > 5) && <span className="text-xs text-gray-400 dark:text-slate-500 self-center">+{wh.bins.length - 5} more</span>}
            </div>

            {/* 🟢 SECURED: Add Bin Form */}
            {checkAccess('warehouse.manage') && (
                <form onSubmit={handleAddBin} className="flex gap-2 mt-4">
                    <input 
                        type="text" 
                        placeholder="Add Bin..." 
                        value={newBin}
                        onChange={(e) => setNewBin(e.target.value)}
                        className="flex-1 border border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#0d1117] text-gray-800 dark:text-white rounded px-2.5 py-1 text-sm outline-none focus:border-blue-500"
                    />
                    <button type="submit" className="bg-gray-800 dark:bg-slate-700 text-white px-3 py-1 rounded text-sm hover:bg-gray-900 dark:hover:bg-slate-600 transition">+</button>
                </form>
            )}

            <div className="mt-4 pt-3 border-t border-gray-100 dark:border-[#30363d] text-center">
                <Link to={`/admin/warehouse-management/${wh.id}`} className="text-blue-600 dark:text-blue-400 text-sm font-medium hover:underline">
                    View Inventory & History &rarr;
                </Link>
            </div>
        </div>
    );
};

const WarehouseManagementPage = () => {
    const { warehouses, saveWarehouse, addBinToWarehouse } = useInventory();
    const [newWarehouseName, setNewWarehouseName] = useState("");
    const navigate = useNavigate();

    // 🟢 GET CHECKER
    const { checkAccess } = useAuth();

    // 🟢 PAGE GUARD
    useEffect(() => {
        if (!checkAccess('warehouse.view')) {
            toast.error("Access Denied.");
            navigate('/admin/dashboard');
        }
    }, [checkAccess, navigate]);

    const handleCreateWarehouse = async (e) => {
        e.preventDefault();
        if (!newWarehouseName.trim()) return;
        await saveWarehouse({ name: newWarehouseName });
        setNewWarehouseName("");
        toast.success("Warehouse Created!");
    };

    if (!checkAccess('warehouse.view')) return null;

    return (
        <PageLayout>
            <PageHeader
                icon={BuildingOffice2Icon}
                iconColor="text-[#E8612C]"
                title="Warehouse Configuration"
                subtitle="Manage physical storage locations and bins"
                onRefresh={() => window.location.reload()}
            />

            {/* 🟢 SECURED: Create Warehouse Form */}
            <RoleGate permission="warehouse.manage">
                <div className="bg-white dark:bg-[#161b22] p-5 rounded-xl shadow-xs border border-gray-200 dark:border-[#30363d] mb-6">
                    <h2 className="text-sm font-bold text-gray-800 dark:text-white mb-3"><T>Add New Warehouse</T></h2>
                    <form onSubmit={handleCreateWarehouse} className="flex gap-3">
                        <input 
                            type="text" 
                            value={newWarehouseName}
                            onChange={(e) => setNewWarehouseName(e.target.value)}
                            placeholder="Enter Warehouse Name (e.g., Central Storage)"
                            className="flex-1 h-9 border border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#0d1117] text-gray-800 dark:text-white px-3 rounded-lg text-xs focus:ring-1 focus:ring-[#E8612C] focus:border-[#E8612C] outline-none"
                            required
                        />
                        <button type="submit" className="bg-[#E8612C] text-white px-5 h-9 rounded-lg text-xs font-bold hover:bg-[#cf4f1d] shadow-xs transition-colors cursor-pointer">
                            Create Warehouse
                        </button>
                    </form>
                </div>
            </RoleGate>

            <h2 className="text-sm font-bold text-gray-800 dark:text-white mb-3"><T>Your Warehouses</T></h2>
            {warehouses.length === 0 ? (
                <div className="text-center py-12 bg-white dark:bg-[#161b22] rounded-xl border border-dashed border-gray-300 dark:border-[#30363d]">
                    <p className="text-gray-500 dark:text-slate-400 text-xs"><T>No warehouses found. Create your first one above.</T></p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {warehouses.map(wh => (
                        <WarehouseCard key={wh.id} wh={wh} addBinToWarehouse={addBinToWarehouse} />
                    ))}
                </div>
            )}
        </PageLayout>
    );
};

export default WarehouseManagementPage;